import React, { useState } from "react";
import { useNavigate as useRouter } from "../../router";
import { Button } from "../../button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../select";
import { Building2, Plus, UserRound } from "../../icons";
import { CreateComposer } from "../create-composer";
import { apiCall } from "../../api/client";
import { customers } from "../../api/contracts/customers";
import { objectHref } from "../../path-label";

type CustomerKind = "organization" | "person";

/**
 * A01 Quick-create (docs/customer-module/PRD.md §6 P1) — the Portfolio's "New
 * customer" primary action, on the shared Linear-style `CreateComposer` (the one
 * create chrome). A <5s flow: pick the kind (Organization/Person via the
 * breadcrumb switcher) + name + optional email; composes a party + a lead via
 * customers.create (email dedup), then opens the new customer's Dossier.
 *
 * No attachment tray here: `customers.create` returns only the party `uid`, and
 * the upload endpoint keys on a numeric id — so the paperclip is intentionally
 * omitted (the composer hides it when `onAttachmentsChange` is absent).
 */
export function QuickCreateCustomer({
  onCreated,
  open: openProp,
  onOpenChange,
  trigger = true,
}: {
  onCreated?: () => void;
  /** Controlled open state — omit for the default self-triggered dialog. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Render the built-in "New customer" trigger button. Default true; pass
   *  `false` when controlling `open` externally (e.g. the section cold-start). */
  trigger?: boolean;
} = {}) {
  const router = useRouter();
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (v: boolean) => (onOpenChange ? onOpenChange(v) : setOpenState(v));
  const [kind, setKind] = useState<CustomerKind>("organization");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [createMore, setCreateMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setName("");
    setEmail("");
    setError(null);
  }
  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      reset();
      setKind("organization");
    }
  }

  function submit() {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const res = await apiCall(customers.create, {
          body: { kind, name: name.trim(), email: email.trim() || undefined },
        });
        onCreated?.();
        if (createMore) {
          reset();
          router.refresh();
          return;
        }
        setOpen(false);
        reset();
        router.push(objectHref(res.uid, "customers"));
      } catch {
        setError("Couldn't create the customer — please try again.");
      } finally {
        setBusy(false);
      }
    })();
  }

  const typeSwitcher = (
    <Select value={kind} onValueChange={(v) => setKind(v as CustomerKind)}>
      <SelectTrigger variant="pill" size="sm" aria-label="Type" className="gap-1.5">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="organization">
          <Building2 />
          Organization
        </SelectItem>
        <SelectItem value="person">
          <UserRound />
          Person
        </SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <>
      {trigger ? (
        <Button
          size="icon-tb"
          aria-label="New customer"
          title="New customer"
          onClick={() => setOpen(true)}
        >
          <Plus className="size-4" />
        </Button>
      ) : null}
      <CreateComposer
        open={open}
        onOpenChange={handleOpenChange}
        context={typeSwitcher}
        heading="New customer"
        title={name}
        onTitleChange={setName}
        titlePlaceholder={kind === "organization" ? "Acme Inc." : "Jane Doe"}
        createMore={createMore}
        onCreateMoreChange={setCreateMore}
        submitLabel="Create customer"
        onSubmit={submit}
        canSubmit={name.trim().length > 0}
        pending={busy}
        error={error}
      >
        {/* Email is the dedup key — a single borderless line under the name,
            matching the composer's title/description rhythm. */}
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email (optional — de-duplicates against existing customers)"
          className="w-full border-0 bg-transparent p-0 type-body text-foreground outline-none placeholder:text-muted-foreground/60"
        />
      </CreateComposer>
    </>
  );
}
